import {
  IntensityByMetric,
  IntensityCoverage,
  IntensityCoverageCell,
  IntensityMetric,
  IntensityScale,
  IntensityYoy,
  IntensityYoyReason,
  TrendValue,
} from "../../../models/dto/cat15/cat15.types";
import { INTENSITY_METRICS } from "../../../components/cat15/labels";

/** Below this share of included outstanding the KPI greys its value and warns. */
export const LOW_COVERAGE_PCT = 50;

// A rank-change sentence names at most this many movers, then counts the rest.
const MAX_MOVES = 3;

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Descending by default; a missing value sorts last in both directions and ties keep their input order. */
export const compareNullsLast =
  <T>(valueOf: (item: T) => number | null, direction: "asc" | "desc" = "desc") =>
  (a: T, b: T): number => {
    const x = valueOf(a);
    const y = valueOf(b);
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    return direction === "desc" ? y - x : x - y;
  };

/** Tier colour for an intensity; an unknown value gets no tier. */
export const tierColor = (value: number | null, scale?: IntensityScale): string | undefined => {
  if (value == null || !scale) return undefined;
  if (value >= scale.high) return "var(--red)";
  if (value >= scale.low) return "var(--yellow)";
  return "var(--green)";
};

export const intensityText = (value: number | null): string => (value == null ? "—" : value.toFixed(2));

export interface BasisOption {
  value: IntensityMetric;
  disabled: boolean;
}

/** A basis no included loan has a figure for is listed but cannot be picked. */
export const basisOptions = (coverage: IntensityCoverage): BasisOption[] =>
  INTENSITY_METRICS.map((value) => ({ value, disabled: coverage[value].loans === 0 }));

export type CoverageState = "full" | "partial" | "low" | "none";

export const coverageState = ({ loans, totalLoans, outstandingPct }: IntensityCoverageCell): CoverageState => {
  if (loans === 0) return "none";
  if (loans >= totalLoans) return "full";
  return outstandingPct < LOW_COVERAGE_PCT ? "low" : "partial";
};

/** A card's coverage line over the loans behind its rows; empty when every one has the figure. */
export const coverageText = (cells: IntensityCoverageCell[], t: Translate, basis: string): string => {
  const loans = cells.reduce((sum, c) => sum + c.loans, 0);
  const total = cells.reduce((sum, c) => sum + c.totalLoans, 0);
  return loans < total ? t("Basis-Coverage-Card", { loans, total, basis }) : "";
};

/** The KPI pill for a basis: the change over loans carrying it in both years, or the reason there is none. */
export const basisTrend = (
  value: number | null,
  yoy: IntensityYoy,
): { trend: TrendValue; suppressed: IntensityYoyReason | null } => ({
  trend: {
    value: value ?? 0,
    delta: yoy.changePct == null ? 0 : Math.round(yoy.changePct * 10) / 10,
    deltaUnit: "%",
    higherIsBetter: false,
  },
  suppressed: yoy.changePct == null ? yoy.reason ?? "no_prior_data" : null,
});

export interface RankRow {
  label: string;
  byMetric: IntensityByMetric;
}

export interface RankShift {
  moves: { label: string; by: number }[];
  more: number;
  entered: string[];
  left: string[];
  missing: string[];
}

/** How a card's top `limit` bars re-rank under a basis vs per outstanding (a tie never counts); null when nothing differs. */
export const rankShift = (rows: RankRow[], basis: IntensityMetric, limit = rows.length): RankShift | null => {
  if (basis === "outstanding") return null;
  const top = (metric: IntensityMetric) =>
    [...rows].sort(compareNullsLast((r: RankRow) => r.byMetric[metric])).slice(0, limit);
  const byOutstanding = top("outstanding");
  const byBasis = top(basis);
  const inOutstanding = new Set(byOutstanding.map((r) => r.label));
  const inBasis = new Set(byBasis.map((r) => r.label));
  const valued = (r: RankRow) => r.byMetric.outstanding != null && r.byMetric[basis] != null;
  // Only ever read for valued rows.
  const valueOf = (r: RankRow, metric: IntensityMetric) => r.byMetric[metric] as number;
  const rowsAbove = (row: RankRow, metric: IntensityMetric, orTied: boolean) =>
    rows.filter((r) => {
      const v = r.byMetric[metric];
      return r !== row && v != null && (orTied ? v >= valueOf(row, metric) : v > valueOf(row, metric));
    }).length;
  // In or out of the top list whichever way its ties were broken.
  const surelyIn = (r: RankRow, metric: IntensityMetric) => rowsAbove(r, metric, true) < limit;
  const surelyOut = (r: RankRow, metric: IntensityMetric) => rowsAbove(r, metric, false) >= limit;

  const missing = [...byOutstanding, ...byBasis.filter((r) => !inOutstanding.has(r.label))]
    .filter((r) => r.byMetric[basis] == null)
    .map((r) => r.label);
  const entered = byBasis
    .filter((r) => valued(r) && !inOutstanding.has(r.label) && surelyIn(r, basis) && surelyOut(r, "outstanding"))
    .map((r) => r.label);
  const left = byOutstanding
    .filter((r) => valued(r) && !inBasis.has(r.label) && surelyIn(r, "outstanding") && surelyOut(r, basis))
    .map((r) => r.label);

  // A row moves up by each row it now strictly passes and down by each that strictly passes it.
  const shared = byOutstanding.filter((r) => valued(r) && inBasis.has(r.label));
  const moves = shared
    .map((row) => ({
      label: row.label,
      by: shared.reduce((by, other) => {
        const wasAbove = Math.sign(valueOf(other, "outstanding") - valueOf(row, "outstanding"));
        const isAbove = Math.sign(valueOf(other, basis) - valueOf(row, basis));
        return wasAbove * isAbove < 0 ? by + wasAbove : by;
      }, 0),
    }))
    .filter((m) => m.by !== 0)
    .sort((a, b) => Math.abs(b.by) - Math.abs(a.by));

  if (!moves.length && !entered.length && !left.length && !missing.length) return null;
  return { moves: moves.slice(0, MAX_MOVES), more: Math.max(0, moves.length - MAX_MOVES), entered, left, missing };
};

/** The footer sentence for a rank shift, e.g. "Ranking differs from per outstanding: Sriracha Power ↑3, Eastern Seaboard ↓2". */
export const rankShiftText = (shift: RankShift, t: Translate, basis: string, limit: number): string => {
  const moves = shift.moves.map((m) => `${m.label} ${m.by > 0 ? "↑" : "↓"}${Math.abs(m.by)}`).join(", ");
  return [
    moves && t("Rank-Change", { moves: shift.more ? `${moves} ${t("Rank-Change-More", { count: shift.more })}` : moves }),
    shift.entered.length ? t("Rank-Enter", { names: shift.entered.join(", "), count: limit }) : "",
    shift.left.length ? t("Rank-Leave", { names: shift.left.join(", "), count: limit }) : "",
    shift.missing.length ? t("Rank-Missing", { basis, names: shift.missing.join(", ") }) : "",
  ]
    .filter(Boolean)
    .join(" · ");
};
